/**
 * Studio / test fixture for ScenarioExplainerClip: AAPL long call through an
 * IV crush ("The stock went up. Why did the call lose value?").
 *
 * In production these numbers come from the Praevue engine via the API; here a
 * small Black-Scholes model generates a self-consistent, deterministic example.
 */

const STRIKE = 230;
const RATE = 0.04;
const ENTRY_SPOT = 227.5;
const ENTRY_IV = 0.32;
const ENTRY_DTE = 30;
const NOW_SPOT = 230.5;
const NOW_IV = 0.24;
const NOW_DTE = 28;
const CONTRACT = 100;

function normCdf(x) {
  // Abramowitz-Stegun 26.2.17
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989423 * Math.exp((-x * x) / 2);
  const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  return x > 0 ? 1 - p : p;
}

function bsCall(spot, strike, years, rate, iv) {
  if (years <= 0) return Math.max(0, spot - strike);
  const sq = iv * Math.sqrt(years);
  const d1 = (Math.log(spot / strike) + (rate + (iv * iv) / 2) * years) / sq;
  const d2 = d1 - sq;
  return spot * normCdf(d1) - strike * Math.exp(-rate * years) * normCdf(d2);
}

const round2 = (v) => Math.round(v * 100) / 100;

const premium = round2(bsCall(ENTRY_SPOT, STRIKE, ENTRY_DTE / 365, RATE, ENTRY_IV));
const debit = round2(premium * CONTRACT);
const breakeven = round2(STRIKE + premium);
const nowValue = bsCall(NOW_SPOT, STRIKE, NOW_DTE / 365, RATE, NOW_IV);
const pnlNow = Math.round((nowValue - premium) * CONTRACT);

const prices = [];
for (let p = 205; p <= 260; p += 2.5) prices.push(p);

const expiration = prices.map((p) => ({
  underlyingPrice: p,
  pnl: round2((Math.max(0, p - STRIKE) - premium) * CONTRACT),
}));
const tPlus0 = prices.map((p) => ({
  underlyingPrice: p,
  pnl: round2((bsCall(p, STRIKE, NOW_DTE / 365, RATE, NOW_IV) - premium) * CONTRACT),
}));

export const aaplLongCallScenarioProps = {
  ticker: "AAPL",
  strategyLabel: "Long Call",
  legsSummary: `Buy 1 AAPL ${STRIKE} call, ${ENTRY_DTE} days to expiration, at $${premium.toFixed(2)}`,
  spot: ENTRY_SPOT,
  numbers: {
    maxLoss: debit,
    maxGain: "Unlimited",
    breakevens: [breakeven],
    netDebitCredit: debit,
  },
  payoff: { expiration, tPlus0 },
  hook: "The stock went up. Why did the call lose value?",
  beats: [
    {
      text: `AAPL climbed from $${ENTRY_SPOT.toFixed(2)} to $${NOW_SPOT.toFixed(2)} after earnings.`,
      emphasis: { value: NOW_SPOT - ENTRY_SPOT, format: "signedCurrency", label: "Stock move" },
      markerPrice: NOW_SPOT,
    },
    {
      text: `But implied volatility dropped from ${Math.round(ENTRY_IV * 100)}% to ${Math.round(NOW_IV * 100)}%, so the option got cheaper.`,
      emphasis: { value: Math.round((NOW_IV - ENTRY_IV) * 100), format: "signedNumber", label: "IV points" },
      markerPrice: NOW_SPOT,
    },
    {
      text: "Less volatility plus two days of time decay outweighed the move.",
      emphasis: { value: pnlNow, format: "signedCurrency", label: "Modeled P&L today" },
      markerPrice: NOW_SPOT,
    },
    {
      text: `At expiration, AAPL needs to finish above $${breakeven.toFixed(2)} to profit.`,
      emphasis: { value: breakeven, format: "price", label: "Breakeven at expiration" },
      markerPrice: breakeven,
    },
  ],
  cta: { text: "Try it yourself", url: "praevue.com/s/demo01" },
  dataAsOf: "Oct 1, 2026 4:00 PM ET",
  disclaimer:
    "Options involve risk and are not suitable for all investors. This example is hypothetical and for illustration only.",
  voiceover: null,
  brand: {
    name: "Praevue",
    logoUrl: null,
  },
};

/** Composition defaultProps shape: the clip lives under `clip` (see parseClipInputProps). */
export const aaplLongCallClipInputProps = { clip: aaplLongCallScenarioProps };
